<script setup>
  import { computed, watch, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
  import { FormField, FormItem, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { useTemplateStore } from "@stores/mailing/template";
  import { useTemplatesStore } from "@stores/mailing/templates";

  const router = useRouter();

  const templatesStore = useTemplatesStore();
  const { getTemplateOptions } = templatesStore;
  const { templateOptions } = storeToRefs(templatesStore);

  const templateStore = useTemplateStore();
  const { template, isNewTemplateVisible } = storeToRefs(templateStore);
  const { createTemplate, resetTemplate } = templateStore;

  const initialValues = { name: "", base_template_id: null };

  const validationSchema = toTypedSchema(
    z.object({
      name: z.string({ required_error: "Please input template name" }).min(1, "Please input template name"),
      base_template_id: z.union([z.string(), z.number()]).nullable().optional(),
    }),
  );

  const { handleSubmit, values, resetForm } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues,
  });

  const isCreationDisabled = computed(() => !values.name);

  watch(isNewTemplateVisible, async (visible) => {
    if (visible) {
      resetForm({ values: { ...initialValues } });
      await getTemplateOptions({ base: [true], active: [true], perPage: 100 });
    } else {
      resetTemplate();
    }
  });

  const handleCancel = () => {
    isNewTemplateVisible.value = false;
  };

  const onSubmit = handleSubmit(async (formValues) => {
    template.value = { ...template.value, ...formValues };
    await createTemplate();
    isNewTemplateVisible.value = false;
    router.push({
      name: "TemplateEdit",
      params: { token: template.value.token },
    });
  });

  onMounted(() => {
    resetTemplate();
  });
</script>

<template>
  <Dialog v-model:open="isNewTemplateVisible">
    <DialogContent :show-close-button="false">
      <DialogHeader>
        <DialogTitle>Create a new template</DialogTitle>
      </DialogHeader>

      <form
        class="space-y-4"
        @submit.prevent="onSubmit">
        <FormField
          v-slot="{ componentField }"
          name="name">
          <FormItem>
            <FormLabel>Name <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-if="templateOptions.length > 0"
          v-slot="{ componentField }"
          name="base_template_id">
          <FormItem>
            <FormLabel>Base Template</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="templateOptions"
                filterable
                placeholder="Select a base template" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </form>

      <DialogFooter>
        <Button
          variant="outline"
          @click="handleCancel">
          Cancel
        </Button>
        <Button
          :disabled="isCreationDisabled"
          @click="onSubmit">
          Create template
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
